import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { auth as authApi, devices as devicesApi } from "../services/api";
import { isNative, registerPush, platform } from "../utils/native";

const AuthContext = createContext(null);

const TOKEN_KEY = "pinned_token";

// Best-effort push token registration (no-op on web)
async function tryRegisterPush(authToken) {
  if (!isNative) return;
  try {
    const pushToken = await registerPush();
    if (pushToken) {
      await devicesApi.register(authToken, pushToken, platform);
    }
  } catch {
    // Push registration is best-effort — don't block auth
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(!!localStorage.getItem(TOKEN_KEY));

  // Load user from stored token on mount
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    authApi
      .me(token)
      .then((userData) => {
        setUser(userData);
        // Re-register push token on app launch (token may have changed)
        tryRegisterPush(token);
      })
      .catch(() => {
        // Token invalid/expired
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const login = useCallback(async (email, password) => {
    const data = await authApi.login(email, password);
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
    tryRegisterPush(data.token);
    return { user: data.user, needsVerification: data.needsVerification };
  }, []);

  const register = useCallback(async (email, password, username, firstName, lastName) => {
    const data = await authApi.register(email, password, username, firstName, lastName);
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
    tryRegisterPush(data.token);
    return data.user;
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const userData = await authApi.me(token);
      setUser(userData);
    } catch {}
  }, [token]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user, token, loading, login, register, logout, refreshUser, isAuthenticated: !!user,
  }), [user, token, loading, login, register, logout, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
